<template>
  <Component
    :is="maxSmall ? Drawer : Dialog"
    :open="open"
    @update:open="$emit('update:open', $event)"
    variant="log"
    :size="maxSmall ? 'full' : maxTablet ? 600 : 700"
    :showFooter="false"
  >
    <slot />
    <template #header>
      <Component
        :is="maxTablet ? DrawerHeader : DialogHeader"
        :title="$t('components.task_details.material_log.title')"
        :description="
          maxTablet
            ? undefined
            : $t('components.task_details.material_log.description')
        "
        class="bg-primary"
        :class="{ 'px-2 border-b': maxTablet }"
      />
      <div v-if="!maxTablet" class="h-5 bg-primary border-b" />
    </template>
    <template #content>
      <div
        class="flex flex-col items-center grow overflow-y-auto"
        :class="{ 'md:p-3 md:gap-2': task.material?.length }"
      >
        <TaskLogItems
          v-if="task.material?.length"
          :items="task.material"
          type="material"
          :canDelete="canRemoveMaterial"
          @delete="removeMaterial"
        />
        <div
          v-else
          class="flex flex-col items-center justify-center grow p-6 gap-6"
        >
          <img
            src="/images/empty/materials.svg"
            :alt="$t('components.task_details.material_log.empty_state.title')"
          />
          <div class="flex flex-col items-center justify-center gap-3">
            <div class="max-w-88 flex flex-col items-center gap-1 text-center">
              <span
                class="text-primary text-style-base lg:text-style-lg font-semibold"
              >
                {{
                  $t("components.task_details.material_log.empty_state.title")
                }}
              </span>
              <span
                class="text-tertiary text-style-base lg:text-style-sm font-normal"
              >
                {{
                  $t(
                    "components.task_details.material_log.empty_state.description"
                  )
                }}
              </span>
            </div>
            <DialogClose
              v-if="canLogMaterial"
              :as="Button"
              :label="
                $t('components.task_details.material_log.empty_state.button')
              "
              variant="secondary"
              type="gray"
              :size="maxTablet ? 'xl' : 'sm'"
              @click="$emit('logMaterial')"
            />
          </div>
        </div>
      </div>
    </template>
  </Component>
</template>

<script setup>
import route from "ziggy-js";
import { computed, inject } from "vue";
import { router } from "@inertiajs/vue3";
import { Button } from "@/SharedComponents/Buttons";
import { Dialog, DialogClose, DialogHeader } from "@/SharedComponents/Dialog";
import { Drawer, DrawerHeader } from "@/SharedComponents/Drawer";
import { TaskLogItems } from "@/SharedComponents/Tasks/TaskDetailsParts";
import { toast } from "@/SharedComponents/Toaster";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { useI18n } from "vue-i18n";

const { maxSmall, maxTablet } = useBreakpoints();
const { t } = useI18n();

defineEmits(["update:open", "logMaterial"]);

const props = defineProps({
  open: { type: Boolean, default: undefined },
  task: { type: Object, required: true },
  canLogMaterial: { type: Boolean, required: false },
});

const { hasPermission, permissions } = inject("permissions");
const emitter = inject("emitter");

const canRemoveMaterial = computed(() =>
  hasPermission(permissions.deleteMaterial, props.task.tenant.id)
);

function removeMaterial(material) {
  if (!canRemoveMaterial.value) {
    return;
  }

  router.delete(route("tasks.material.destroy", material.id), {
    onSuccess: () => {
      toast.success(t("toasts.tasks.remove_material.success"));
      emitter.emit("reload-task-details", props.task);
    },
    onError: () => {
      toast.error(t("toasts.tasks.remove_material.error"));
    },
  });
}
</script>
